import { ROUTES } from "@app/constants";
import { IGetReport } from "@shared/lib/types/reports";
import { Button, Table } from "@shared/ui";
import { TextWithTooltip } from "@shared/ui/TextWithTooltip";
import type { ColumnsType, TableProps } from "antd/es/table";
import { BarChart3, PieChart } from "lucide-react";
import { JSX, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ReportsTable.module.scss";

interface ReportsTableProps {
  reports: IGetReport[];
  isFetching: boolean;
  pageNo: number;
  pageSize: number;
  totaldata: number;
  onPageChange: (page: number) => void;
  className?: string;
  title?: JSX.Element;
  totalPages: number;
}

export function ReportsTable({
  reports,
  isFetching,
  pageNo,
  pageSize,
  totaldata,
  onPageChange,
  totalPages,
  className,
  title,
}: ReportsTableProps) {
  const navigate = useNavigate();

  const columns: ColumnsType<IGetReport> = useMemo(
    () => [
      {
        title: "Report Name",
        dataIndex: "name",
        key: "reportName",
        render: (_value, record: IGetReport) => {
          const isCustom = record.type === "Custom";
          return (
            <div className={styles.reportNameCell}>
              <div
                className={`${styles.reportIcon} ${
                  isCustom ? styles.reportIconCustom : styles.reportIconStandard
                }`}
              >
                <BarChart3 size={16} />
              </div>
              <span className={styles.reportName}>{record.name}</span>
            </div>
          );
        },
      },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        render: (_value, record: IGetReport) => {
          const isCustom = record.type === "Custom";
          return (
            <span
              className={`${styles.typeBadge} ${
                isCustom ? styles.typeBadgeCustom : styles.typeBadgeStandard
              }`}
            >
              {record.type === "Custom" ? "Custom" : "Campaign"}
            </span>
          );
        },
      },
      {
        title: "Campaign Source",
        dataIndex: "campaign",
        key: "campaign",
        render: (_value, record: IGetReport) => {
          const isCustom = record.type === "Custom";

          if (isCustom) {
            return <span> -</span>;
          }

          return (
            <TextWithTooltip
              text={record.brief?.name || "-"}
              // textClassName={styles.campaignText}
            />
          );
        },
      },
      {
        title: "Date Created",
        dataIndex: "createdAt",
        key: "dateCreated",
        render: (_value, record: IGetReport) => {
          const date = new Date(record.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
          return <span>{date}</span>;
        },
      },
      {
        title: "Media Counts",
        dataIndex: "creators",
        key: "creators",
        align: "center",
        render: (_value, record: IGetReport) => {
          const creatorsCount = record.noOfMedias || 0;
          return (
            <span className={styles.creatorsBadge}>{creatorsCount}</span>
          );
        },
      },
    ],
    []
  );

  const dataSource = useMemo(
    () =>
      reports.map((report) => ({
        ...report,
        key: report._id,
      })),
    [reports]
  );

  const handleTableChange: TableProps<IGetReport>["onChange"] = (
    pagination
  ) => {
    if (pagination?.current) {
      onPageChange(pagination.current);
    }
  };

  return (
    <div className={`${className || ""}`}>
      <Table<IGetReport>
        columns={columns}
        title={() => title}
        dataSource={dataSource}
        loading={isFetching}
        pagination={{
          current: pageNo,
          pageSize,
          total: totaldata,
          showSizeChanger: false,
          // showTotal: (total) => (
          //   <span className={styles.paginationText}>
          //     Showing {total} reports
          //   </span>
          // ),
          className: styles.paginationBar,
          itemRender: (_page, type, originalElement) => {
            if (type === "prev") {
              return (
                <Button
                  size="small"
                  className={styles.paginationNav}
                  disabled={pageNo <= 1}
                  onClick={() => {
                    if (pageNo > 1) {
                      onPageChange(pageNo - 1);
                    }
                  }}
                >
                  Previous
                </Button>
              );
            }
            if (type === "next") {
              return (
                <Button
                  size="small"
                  disabled={pageNo >= totalPages}
                  className={styles.paginationNav}
                  onClick={() => {
                    if (pageNo < totalPages) {
                      onPageChange(pageNo + 1);
                    }
                  }}
                >
                  Next
                </Button>
              );
            }
            return originalElement;
          },
        }}
        onChange={handleTableChange}
        rowKey="_id"
        scroll={{ x: true }}
        locale={{
          emptyText: (
            <div className={styles.emptyState}>
              <PieChart size={32} className={styles.emptyIcon} />
              <p className={styles.emptyText}>
                No reports found matching your search.
              </p>
            </div>
          ),
        }}
        onRow={(record) => ({
          onClick: () => {
            navigate(ROUTES.REPORT_DETAIL.replace(":reportId", record._id));
          },
          className: styles.tableRow,
        })}
        className={styles.campaignTable}
      />
    </div>
  );
}
